import React, { useState, useEffect } from 'react';

const AsymmetricMatches = ({ teams, results, setResults }) => {
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [showMatchOrder, setShowMatchOrder] = useState(false);
  const [matchOrder, setMatchOrder] = useState([]);

  // 1チーム対1チームの対戦表を生成（BO3/BO5形式）
  useEffect(() => {
    if (teams.length < 2) {
      setMatches([]);
      return;
    }

    const newMatches = [];
    let matchId = 0;

    // 全チーム同士の対戦を生成
    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        const matchIdStr = `asymmetric-${matchId}`;
        const existingResult = results.find(r => r.matchId === matchIdStr);
        
        newMatches.push({
          id: matchIdStr,
          matchNumber: matchId + 1,
          team1: teams[i],
          team2: teams[j],
          result: existingResult || null
        });
        matchId++;
      }
    }
    setMatches(newMatches);
    
    // 試合順序が設定されていない場合、デフォルト順序を設定
    if (matchOrder.length === 0 && newMatches.length > 0) {
      setMatchOrder(newMatches.map((match, index) => ({ matchId: match.id, order: index + 1 })));
    }
  }, [teams, results, matchOrder.length]);

  // 順位表を計算
  useEffect(() => {
    if (teams.length === 0) {
      setStandings([]);
      return;
    }

    const teamStats = teams.map(team => {
      const teamResults = results.filter(r => 
        r.team1Id === team.id || r.team2Id === team.id
      );

      let matchWins = 0;
      let matchLosses = 0;
      let gameWins = 0;
      let gameLosses = 0;
      let totalPoints = 0;
      let hunterPoints = 0;
      let survivorPoints = 0;

      teamResults.forEach(result => {
        if (result.winner) {
          if (result.winner === team.id) {
            matchWins++;
          } else {
            matchLosses++;
          }
        }

        // 各ゲームの結果を集計
        if (result.games) {
          result.games.forEach(game => {
            if (game.winner) {
              if (game.winner === team.id) {
                gameWins++;
              } else {
                gameLosses++;
              }
            }

            // ポイント計算
            if (game.firstHalf) {
              if (game.firstHalf.hunterTeam === team.id) {
                const points = getHunterPoints(game.firstHalf.killCount);
                hunterPoints += points;
                totalPoints += points;
              } else {
                const points = getSurvivorPoints(game.firstHalf.escapeCount);
                survivorPoints += points;
                totalPoints += points;
              }
            }

            if (game.secondHalf) {
              if (game.secondHalf.hunterTeam === team.id) {
                const points = getHunterPoints(game.secondHalf.killCount);
                hunterPoints += points;
                totalPoints += points;
              } else {
                const points = getSurvivorPoints(game.secondHalf.escapeCount);
                survivorPoints += points;
                totalPoints += points;
              }
            }
          });
        }
      });

      const totalMatches = matchWins + matchLosses;
      const totalGames = gameWins + gameLosses;
      const matchWinRate = totalMatches > 0 ? (matchWins / totalMatches) * 100 : 0;
      const gameWinRate = totalGames > 0 ? (gameWins / totalGames) * 100 : 0;

      return {
        team,
        matchWins,
        matchLosses,
        gameWins,
        gameLosses,
        totalPoints,
        hunterPoints,
        survivorPoints,
        totalMatches,
        totalGames,
        matchWinRate: parseFloat(matchWinRate.toFixed(1)),
        gameWinRate: parseFloat(gameWinRate.toFixed(1))
      };
    });

    // 試合勝利数、ゲーム勝利数、総ポイント順でソート
    teamStats.sort((a, b) => {
      if (b.matchWins !== a.matchWins) return b.matchWins - a.matchWins;
      if (b.gameWins !== a.gameWins) return b.gameWins - a.gameWins;
      return b.totalPoints - a.totalPoints;
    });

    setStandings(teamStats);
  }, [teams, results]);

  // ハンターの得点計算（吊り数に基づく）
  const getHunterPoints = (killCount) => {
    if (killCount === 4) return 5; // 4吊りで5点
    if (killCount === 3) return 3; // 3吊りで3点
    if (killCount === 2) return 2; // 2吊りで2点
    if (killCount === 1) return 1; // 1吊りで1点
    return 0; // 0吊りで0点
  };

  // サバイバーの得点計算（逃げた人数に基づく）
  const getSurvivorPoints = (escapeCount) => {
    if (escapeCount === 4) return 5; // 4人逃げで5点
    if (escapeCount === 3) return 3; // 3人逃げで3点
    if (escapeCount === 2) return 2; // 2人逃げで2点
    if (escapeCount === 1) return 1; // 1人逃げで1点
    return 0; // 0人逃げで0点
  };

  // ゲームの勝者を決定（前半戦+後半戦の合計ポイント）
  const determineGameWinner = (firstHalf, secondHalf, team1Id, team2Id) => {
    let team1Points = 0;
    let team2Points = 0;

    // 前半戦ポイント
    if (firstHalf.hunterTeam === team1Id) {
      team1Points += getHunterPoints(firstHalf.killCount);
      team2Points += getSurvivorPoints(firstHalf.escapeCount);
    } else {
      team2Points += getHunterPoints(firstHalf.killCount);
      team1Points += getSurvivorPoints(firstHalf.escapeCount);
    }

    // 後半戦ポイント
    if (secondHalf.hunterTeam === team1Id) {
      team1Points += getHunterPoints(secondHalf.killCount);
      team2Points += getSurvivorPoints(secondHalf.escapeCount);
    } else {
      team2Points += getHunterPoints(secondHalf.killCount);
      team1Points += getSurvivorPoints(secondHalf.escapeCount);
    }

    if (team1Points > team2Points) return team1Id;
    if (team2Points > team1Points) return team2Id;
    return null; // 引き分け
  };

  const recordMatchResult = (matchId, games, match) => {
    // 各ゲームの勝者を決定
    const gamesWithWinners = games.map(game => ({
      ...game,
      winner: determineGameWinner(game.firstHalf, game.secondHalf, match.team1.id, match.team2.id)
    }));

    // 試合全体の勝者を決定
    const team1Wins = gamesWithWinners.filter(game => game.winner === match.team1.id).length;
    const team2Wins = gamesWithWinners.filter(game => game.winner === match.team2.id).length;
    
    let matchWinner = null;
    if (team1Wins > team2Wins) matchWinner = match.team1.id;
    else if (team2Wins > team1Wins) matchWinner = match.team2.id;

    const newResult = {
      matchId,
      team1Id: match.team1.id,
      team2Id: match.team2.id,
      games: gamesWithWinners,
      winner: matchWinner,
      team1Wins,
      team2Wins,
      timestamp: new Date().toISOString()
    };

    const updatedResults = results.filter(r => r.matchId !== matchId);
    setResults([...updatedResults, newResult]);
  };

  const clearResult = (matchId) => {
    setResults(results.filter(r => r.matchId !== matchId));
  };

  const getMatchProgress = () => {
    const totalMatches = matches.length;
    const completedMatches = results.filter(r => r.winner !== null).length;
    return { total: totalMatches, completed: completedMatches };
  };

  // 試合順序管理機能
  const updateMatchOrder = (matchId, newOrder) => {
    setMatchOrder(prevOrder => 
      prevOrder.map(item => 
        item.matchId === matchId 
          ? { ...item, order: parseInt(newOrder) }
          : item
      ).sort((a, b) => a.order - b.order)
    );
  };

  const resetMatchOrder = () => {
    setMatchOrder(matches.map((match, index) => ({ matchId: match.id, order: index + 1 })));
  };

  // 順序に従って試合をソート
  const sortedMatches = matches.sort((a, b) => {
    const orderA = matchOrder.find(item => item.matchId === a.id)?.order || 999;
    const orderB = matchOrder.find(item => item.matchId === b.id)?.order || 999;
    return orderA - orderB;
  });

  const progress = getMatchProgress();

  if (teams.length < 2) {
    return (
      <div className="asymmetric-matches">
        <div className="section-header">
          <h2>⚔️ 総当たり戦</h2>
          <p>1チーム vs 1チーム（BO3/BO5形式）</p>
        </div>
        <div className="empty-state">
          <p>総当たり戦を開始するには、最低2チームが必要です</p>
          <p>チーム管理タブからプロチームを追加してください</p>
        </div>
      </div>
    );
  }

  return (
    <div className="asymmetric-matches">
      <div className="section-header">
        <h2>⚔️ 総当たり戦</h2>
        <p>1チーム vs 1チーム（BO3/BO5形式）</p>
        <div className="scoring-info">
          <div className="scoring-rule">
            <strong>得点システム:</strong>
            <br />
            ハンター: 4吊り=5点, 3吊り=3点, 2吊り=2点, 1吊り=1点, 0吊り=0点
            <br />
            サバイバー: 4人逃げ=5点, 3人逃げ=3点, 2人逃げ=2点, 1人逃げ=1点, 0人逃げ=0点
            <br />
            <strong>勝敗判定:</strong> 各ゲーム（前半戦+後半戦）の合計ポイントで勝者決定、先に規定数勝利で試合勝利
          </div>
        </div>
      </div>

      {/* 進捗状況 */}
      <div className="progress-section">
        <div className="progress-card">
          <h3>📈 進捗状況</h3>
          <div className="progress-info">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="progress-text">
              {progress.completed} / {progress.total} 試合完了 
              ({progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0}%)
            </div>
          </div>
        </div>
      </div>

      {/* 対戦表 */}
      <div className="matches-section">
        <div className="matches-header">
          <h3>🥊 対戦表</h3>
          <button 
            onClick={() => setShowMatchOrder(!showMatchOrder)}
            className="order-button"
          >
            📋 試合順序設定
          </button>
        </div>

        {/* 試合順序設定パネル */}
        {showMatchOrder && (
          <div className="match-order-panel">
            <div className="order-header">
              <h4>試合順序設定</h4>
              <div className="order-actions">
                <button onClick={resetMatchOrder} className="reset-order-button">
                  🔄 順序をリセット
                </button>
                <button onClick={() => setShowMatchOrder(false)} className="close-order-button">
                  ❌ 閉じる
                </button>
              </div>
            </div>
            <div className="order-list">
              {sortedMatches.map((match) => {
                const currentOrder = matchOrder.find(item => item.matchId === match.id)?.order || 1;
                return (
                  <div key={match.id} className="order-item">
                    <div className="match-info">
                      <span className="match-teams">{match.team1.name} vs {match.team2.name}</span>
                    </div>
                    <div className="order-control">
                      <label>順序:</label>
                      <input
                        type="number"
                        min="1"
                        max={matches.length}
                        value={currentOrder}
                        onChange={(e) => updateMatchOrder(match.id, e.target.value)}
                        className="order-input"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="matches-grid">
          {sortedMatches.map((match, index) => {
            const displayOrder = matchOrder.find(item => item.matchId === match.id)?.order || index + 1;
            return (
            <div key={match.id} className="asymmetric-match-card">
              <div className="match-header">
                <span className="match-number">第{displayOrder}試合</span>
                {match.result && match.result.winner && (
                  <span className="match-status completed">✅ 完了</span>
                )}
              </div>
              
              <div className="team-vs-team">
                <div className="team-display">
                  <div className="team-name">{match.team1.name}</div>
                  <div className="vs-divider">VS</div>
                  <div className="team-name">{match.team2.name}</div>
                </div>
              </div>

              {match.result && match.result.winner ? (
                <div className="match-result">
                  <div className="match-winner">
                    <h4>🏆 試合勝者: {match.result.winner === match.team1.id ? match.team1.name : match.team2.name}</h4>
                    <div className="score-summary">
                      {match.team1.name} {match.result.team1Wins} - {match.result.team2Wins} {match.team2.name}
                    </div>
                  </div>
                  
                  <div className="games-results">
                    {match.result.games.map((game, gameIndex) => (
                      <div key={gameIndex} className="game-result">
                        <div className="game-header">
                          <strong>ゲーム{gameIndex + 1}</strong>
                          {game.winner && (
                            <span className="game-winner">
                              勝者: {game.winner === match.team1.id ? match.team1.name : match.team2.name}
                            </span>
                          )}
                        </div>
                        <div className="half-results">
                          <div className="half-result">
                            <strong>前半戦:</strong> 
                            <div className="result-details">
                              ハンター: {game.firstHalf.hunterTeam === match.team1.id ? match.team1.name : match.team2.name}
                              ({game.firstHalf.killCount}吊り - {getHunterPoints(game.firstHalf.killCount)}点)
                              <br />
                              サバイバー: {game.firstHalf.hunterTeam === match.team1.id ? match.team2.name : match.team1.name}
                              ({game.firstHalf.escapeCount}人逃げ - {getSurvivorPoints(game.firstHalf.escapeCount)}点)
                            </div>
                          </div>
                          <div className="half-result">
                            <strong>後半戦:</strong> 
                            <div className="result-details">
                              ハンター: {game.secondHalf.hunterTeam === match.team1.id ? match.team1.name : match.team2.name}
                              ({game.secondHalf.killCount}吊り - {getHunterPoints(game.secondHalf.killCount)}点)
                              <br />
                              サバイバー: {game.secondHalf.hunterTeam === match.team1.id ? match.team2.name : match.team1.name}
                              ({game.secondHalf.escapeCount}人逃げ - {getSurvivorPoints(game.secondHalf.escapeCount)}点)
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => clearResult(match.id)}
                    className="clear-button"
                  >
                    🔄 結果をクリア
                  </button>
                </div>
              ) : (
                <MatchResultForm 
                  match={match}
                  onSubmit={(games) => 
                    recordMatchResult(match.id, games, match)
                  }
                  getHunterPoints={getHunterPoints}
                  getSurvivorPoints={getSurvivorPoints}
                  determineGameWinner={determineGameWinner}
                />
              )}
            </div>
          )})}
        </div>
      </div>

      {/* 順位表 */}
      {standings.length > 0 && (
        <div className="standings-section">
          <h3>🏆 現在の順位表</h3>
          <div className="asymmetric-standings-table">
            <div className="table-header">
              <div className="rank-col">順位</div>
              <div className="name-col">チーム名</div>
              <div className="match-record-col">試合成績</div>
              <div className="game-record-col">ゲーム成績</div>
              <div className="points-col">総ポイント</div>
              <div className="winrate-col">試合勝率</div>
            </div>
            {standings.map((standing, index) => (
              <div key={standing.team.id} className="table-row">
                <div className="rank-col">
                  <span className={`rank ${index < 3 ? `rank-${index + 1}` : ''}`}>
                    {index + 1}
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                  </span>
                </div>
                <div className="name-col">{standing.team.name}</div>
                <div className="match-record-col">{standing.matchWins}勝{standing.matchLosses}敗</div>
                <div className="game-record-col">{standing.gameWins}勝{standing.gameLosses}敗</div>
                <div className="points-col">{standing.totalPoints}pt</div>
                <div className="winrate-col">{standing.matchWinRate}%</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// BO3/BO5試合結果入力フォーム
const MatchResultForm = ({ match, onSubmit, getHunterPoints, getSurvivorPoints, determineGameWinner }) => {
  const [matchFormat, setMatchFormat] = useState('BO3'); // BO3 or BO5
  const [games, setGames] = useState([]);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [isMatchComplete, setIsMatchComplete] = useState(false);

  // 試合形式が変更されたときにゲーム配列を初期化
  useEffect(() => {
    const maxGames = matchFormat === 'BO3' ? 3 : 5;
    setGames(Array(maxGames).fill(null).map(() => ({
      firstHalf: { hunterTeam: '', killCount: 0, escapeCount: 4 },
      secondHalf: { hunterTeam: '', killCount: 0, escapeCount: 4 },
      completed: false
    })));
    setCurrentGameIndex(0);
    setIsMatchComplete(false);
  }, [matchFormat]);

  // 試合完了チェック
  useEffect(() => {
    const completedGames = games.filter(game => game.completed);
    if (completedGames.length === 0) return;

    const gamesWithWinners = completedGames.map(game => ({
      ...game,
      winner: determineGameWinner(game.firstHalf, game.secondHalf, match.team1.id, match.team2.id)
    }));

    const team1Wins = gamesWithWinners.filter(game => game.winner === match.team1.id).length;
    const team2Wins = gamesWithWinners.filter(game => game.winner === match.team2.id).length;
    const requiredWins = matchFormat === 'BO3' ? 2 : 3;

    if (team1Wins >= requiredWins || team2Wins >= requiredWins) {
      setIsMatchComplete(true);
    }
  }, [games, match.team1.id, match.team2.id, matchFormat, determineGameWinner]);

  const updateGameResult = (gameIndex, half, field, value) => {
    const newGames = [...games];
    newGames[gameIndex][half][field] = value;
    
    // 吊り数が変更されたら逃げ人数も自動更新
    if (field === 'killCount') {
      newGames[gameIndex][half].escapeCount = 4 - value;
    }
    
    setGames(newGames);
  };

  const completeGame = (gameIndex) => {
    const newGames = [...games];
    newGames[gameIndex].completed = true;
    setGames(newGames);
    
    // 次のゲームに進む
    if (gameIndex < games.length - 1) {
      setCurrentGameIndex(gameIndex + 1);
    }
  };

  const handleSubmit = () => {
    const completedGames = games.filter(game => game.completed);
    onSubmit(completedGames);
  };

  const canSubmitMatch = isMatchComplete;
  const currentGame = games[currentGameIndex];

  if (!currentGame) return null;

  const canCompleteCurrentGame = 
    currentGame.firstHalf.hunterTeam && 
    currentGame.secondHalf.hunterTeam && 
    !currentGame.completed;

  return (
    <div className="bo-result-form">
      <div className="format-selection">
        <label>試合形式:</label>
        <div className="format-buttons">
          <button 
            className={`format-button ${matchFormat === 'BO3' ? 'selected' : ''}`}
            onClick={() => setMatchFormat('BO3')}
          >
            BO3 (2勝先取)
          </button>
          <button 
            className={`format-button ${matchFormat === 'BO5' ? 'selected' : ''}`}
            onClick={() => setMatchFormat('BO5')}
          >
            BO5 (3勝先取)
          </button>
        </div>
      </div>

      <div className="games-progress">
        <h4>ゲーム進行状況</h4>
        <div className="games-status">
          {games.map((game, index) => (
            <div key={index} className={`game-status ${game.completed ? 'completed' : index === currentGameIndex ? 'current' : 'pending'}`}>
              ゲーム{index + 1}
              {game.completed && '✅'}
              {index === currentGameIndex && !game.completed && '⏳'}
            </div>
          ))}
        </div>
      </div>

      {!isMatchComplete && (
        <div className="current-game-form">
          <h4>ゲーム{currentGameIndex + 1} 結果入力</h4>
          
          <div className="half-result-selection">
            <h5>前半戦結果</h5>
            <div className="hunter-selection">
              <label>ハンターチーム:</label>
              <div className="team-buttons">
                <button 
                  className={`team-button ${currentGame.firstHalf.hunterTeam === match.team1.id ? 'selected' : ''}`}
                  onClick={() => updateGameResult(currentGameIndex, 'firstHalf', 'hunterTeam', match.team1.id)}
                >
                  {match.team1.name}
                </button>
                <button 
                  className={`team-button ${currentGame.firstHalf.hunterTeam === match.team2.id ? 'selected' : ''}`}
                  onClick={() => updateGameResult(currentGameIndex, 'firstHalf', 'hunterTeam', match.team2.id)}
                >
                  {match.team2.name}
                </button>
              </div>
            </div>
            <div className="kill-count-selection">
              <label>吊り数:</label>
              <div className="kill-buttons">
                {[0, 1, 2, 3, 4].map(count => (
                  <button
                    key={count}
                    className={`kill-button ${currentGame.firstHalf.killCount === count ? 'selected' : ''}`}
                    onClick={() => updateGameResult(currentGameIndex, 'firstHalf', 'killCount', count)}
                  >
                    {count}吊り ({getHunterPoints(count)}pt)
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="half-result-selection">
            <h5>後半戦結果</h5>
            <div className="hunter-selection">
              <label>ハンターチーム:</label>
              <div className="team-buttons">
                <button 
                  className={`team-button ${currentGame.secondHalf.hunterTeam === match.team1.id ? 'selected' : ''}`}
                  onClick={() => updateGameResult(currentGameIndex, 'secondHalf', 'hunterTeam', match.team1.id)}
                >
                  {match.team1.name}
                </button>
                <button 
                  className={`team-button ${currentGame.secondHalf.hunterTeam === match.team2.id ? 'selected' : ''}`}
                  onClick={() => updateGameResult(currentGameIndex, 'secondHalf', 'hunterTeam', match.team2.id)}
                >
                  {match.team2.name}
                </button>
              </div>
            </div>
            <div className="kill-count-selection">
              <label>吊り数:</label>
              <div className="kill-buttons">
                {[0, 1, 2, 3, 4].map(count => (
                  <button
                    key={count}
                    className={`kill-button ${currentGame.secondHalf.killCount === count ? 'selected' : ''}`}
                    onClick={() => updateGameResult(currentGameIndex, 'secondHalf', 'killCount', count)}
                  >
                    {count}吊り ({getHunterPoints(count)}pt)
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button 
            className={`complete-game-button ${canCompleteCurrentGame ? 'enabled' : 'disabled'}`}
            onClick={() => completeGame(currentGameIndex)}
            disabled={!canCompleteCurrentGame}
          >
            ゲーム{currentGameIndex + 1}完了
          </button>
        </div>
      )}

      {isMatchComplete && (
        <div className="match-complete">
          <h4>🎉 試合完了！</h4>
          <button 
            className="submit-match-button enabled"
            onClick={handleSubmit}
          >
            試合結果を記録
          </button>
        </div>
      )}
    </div>
  );
};

export default AsymmetricMatches;
